import { useMemo } from 'react'

import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_CHAIN_HEALTH } from '~/constants/refetch-intervals'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import { useServerRequests } from '~/hooks/useRequests'

const STALLED_INDEXER_THRESHOLD_SECONDS = 5 * 60

type LatestBlock = { number: string; timestamp: number }

type ChainHealthRaw = {
  chainId: number
  displayName: string | null
  latestBlock: LatestBlock | null
}

type ChainHealth = ChainHealthRaw & { isStalled: boolean }

type ChainHealthApiResponse = { chains: ChainHealthRaw[] }
type ChainHealthData = { chains: ChainHealth[] }

// Staleness is computed at fetch time, not render time, so we never need to
// read the clock during render. Drift between fetch and render is bounded by
// REFETCH_CHAIN_HEALTH (30s) — well under the 5-minute stall threshold.
const isLatestBlockStalled = (
  latestBlock: LatestBlock | null,
  nowSec: number,
): boolean =>
  latestBlock !== null &&
  nowSec - latestBlock.timestamp > STALLED_INDEXER_THRESHOLD_SECONDS

const useChainHealth = () => {
  const { serverGetRequest } = useServerRequests()

  return useQueryWrapper<ChainHealthData>({
    queryKey: [QUERY_KEYS.CHAIN_HEALTH],
    queryFn: async () => {
      const response = await serverGetRequest<ChainHealthApiResponse>({
        path: '/api/chains',
      })

      if (!response) {
        throw new Error('Failed to fetch chain health')
      }

      const nowSec = Math.floor(Date.now() / 1000)
      return {
        chains: response.data.chains.map((chain) => ({
          ...chain,
          isStalled: isLatestBlockStalled(chain.latestBlock, nowSec),
        })),
      }
    },
    refetchInterval: REFETCH_CHAIN_HEALTH,
  })
}

export const useStalledChainIds = (): Set<number> => {
  const { data } = useChainHealth()

  return useMemo(() => {
    if (!data) return new Set()
    return new Set(
      data.chains
        .filter((chain) => chain.isStalled)
        .map((chain) => chain.chainId),
    )
  }, [data])
}
